import {Spinner} from "@heroui/spinner";
import {useQuery} from "@tanstack/react-query";
import {createFileRoute, useSearch} from '@tanstack/react-router';
import {useState} from "react";
import {apiActionImproved} from "../api";
import {config} from "../config/config.ts";

/**
 * Public match-fee payment page, reached from a tokenized link the captain shares.
 *
 * No account, no login: the token *is* the identity (roster players usually aren't ScorePal
 * users). Shows who the share is for and which game, then hands off to Stripe Checkout. Stripe
 * returns here with ?paid=1&session_id=..., which pay-details uses to settle the entry inline if
 * the webhook hasn't landed yet.
 */
export const Route = createFileRoute('/pay/match-fee/$token')({
    component: PayMatchFeePage,
});

function PayMatchFeePage() {
    // Match fees ship dark where the flag is off (production) — a shared link shows a soft
    // notice instead of a live Stripe checkout.
    if (!config.featureEnableMatchFees) {
        return <div className="max-w-md mx-auto mt-20 px-4 text-center text-default-500">
            This payment link isn&apos;t available yet. Please check back soon or contact your organiser.
        </div>;
    }
    const {token} = Route.useParams();
    const search = useSearch({strict: false}) as {paid?: string; session_id?: string};
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const detailsQuery = useQuery({
        queryKey: ['match-fee', token, search.session_id || ''],
        queryFn:  () => apiActionImproved('game-fees-pay-details' as any, {pay_token: token, session_id: search.session_id || null}),
        retry:    false,
    });

    const d = detailsQuery.data as any;

    const pay = async () => {
        setBusy(true);
        setError('');
        try {
            const res: any = await apiActionImproved('game-fees-pay-checkout' as any, {pay_token: token});
            if (res?.checkout_url) {
                window.location.href = res.checkout_url;
                return;
            }
            throw new Error(res?.message || 'Could not start the payment.');
        } catch (e: any) {
            setError(e?.message || 'Could not start the payment.');
            setBusy(false);
        }
    };

    const money = (cents: number, cur?: string) => `${((cents || 0) / 100).toFixed(2)} ${(cur || '').toUpperCase()}`;

    return (
        <div className="flex min-h-screen items-center justify-center bg-[#F6F7FB] px-5 py-10">
            <div className="w-full max-w-md rounded-2xl border border-[#e5e7eb] bg-white p-7 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#082F8A]">ScorePal · Match fee</p>

                {detailsQuery.isLoading ? (
                    <div className="py-10 text-center"><Spinner className="mx-auto" /></div>
                ) : detailsQuery.isError || !d ? (
                    <p className="mt-3 font-inter text-sm text-[#1a1a2e]/70">
                        This payment link is invalid or out of date — ask your captain or the organizer for a fresh one.
                    </p>
                ) : (
                    <>
                        <h1 className="mt-1 text-2xl font-bold text-[#1a1a2e]">{d.name}</h1>
                        {d.game && (
                            <p className="mt-1 text-sm text-[#1a1a2e]/70">
                                {d.game.team_1 && d.game.team_2 ? `${d.game.team_1} vs ${d.game.team_2}` : (d.game.title || 'Match')}
                                {d.game.date ? ` · ${d.game.date}` : ''}{d.game.venue ? ` · ${d.game.venue}` : ''}
                            </p>
                        )}

                        <div className="mt-5 rounded-xl bg-[#F6F7FB] p-4 text-center">
                            <p className="text-xs uppercase tracking-wide text-[#1a1a2e]/50">Your share</p>
                            <p className="text-3xl font-black text-[#1a1a2e]">{money(d.amount_cents, d.currency_code)}</p>
                        </div>

                        {d.status !== 'pending' ? (
                            <div className="mt-5 rounded-xl bg-green-50 p-4 text-center">
                                <p className="font-bold text-green-700">
                                    {d.status === 'waived' ? 'This share was waived — nothing to pay.' : 'Paid — you\'re all set for kickoff. ⚽'}
                                </p>
                            </div>
                        ) : (
                            <>
                                <button
                                    onClick={pay}
                                    disabled={busy}
                                    className="mt-5 w-full rounded-xl bg-[#082F8A] px-4 py-3 font-bold text-white transition hover:bg-[#0a3aa8] disabled:opacity-60"
                                >
                                    {busy ? 'Opening secure checkout…' : 'Pay by card'}
                                </button>
                                <p className="mt-2 text-center text-[11px] text-[#1a1a2e]/50">Card payments are processed securely by Stripe.</p>
                            </>
                        )}

                        {error && <p className="mt-3 text-center text-sm font-semibold text-red-600">{error}</p>}
                    </>
                )}
            </div>
        </div>
    );
}
